import React, { useState, useEffect } from 'react';

const Icon = ({ d, size = 16, strokeWidth = 2, color = 'currentColor', className = '' }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    vehicles: ['M1 3h15v13H1z', 'M16 8l4 4v5h-4z', 'M5 18a2 2 0 100 4 2 2 0 000-4z', 'M12 18a2 2 0 100 4 2 2 0 000-4z'],
    operators: ['M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2', 'M9 11a4 4 0 100-8 4 4 0 000 8z'],
    routes: ['M1 6l7-3 8 3 7-3v15l-7 3-8-3-7 3z', 'M8 3v15', 'M16 6v15'],
    users: ['M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2', 'M9 7a4 4 0 100-8 4 4 0 000 8z', 'M22 21v-2a4 4 0 00-3-3.87', 'M16 3.13a4 4 0 010 7.75'],
    price: ['M2 6h20v12H2z', 'M12 12a3 3 0 100-6 3 3 0 000 6z', 'M6 12h.01', 'M18 12h.01'],
    check: 'M20 6L9 17l-5-5',
    card: ['M1 4h22v16H1z', 'M1 10h22'],
};

const defaultForm = {
    name: '',
    description: '',
    isFree: false,
    price: '',
    pricePeriod: '',
    vehiclesLimit: '',
    operatorsLimit: '',
    routesLimit: '',
    usersLimit: '',
    isActive: true,
};

export default function CreatePricePlan({ editPlan, onSaved, user }) {
    const [form, setForm] = useState(defaultForm);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);

    useEffect(() => {
        if (editPlan) {
            setForm({
                name: editPlan.name || '',
                description: editPlan.description || '',
                isFree: editPlan.isFree || false,
                price: editPlan.price ?? '',
                pricePeriod: editPlan.pricePeriod || '',
                vehiclesLimit: editPlan.vehiclesLimit ?? '',
                operatorsLimit: editPlan.operatorsLimit ?? '',
                routesLimit: editPlan.routesLimit ?? '',
                usersLimit: editPlan.usersLimit ?? '',
                isActive: editPlan.isActive ?? true,
            });
        } else {
            setForm(defaultForm);
        }
    }, [editPlan]);

    const isEditMode = !!editPlan;

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
    };

    const handleSave = async () => {
        if (!form.name.trim()) { showToast('Plan Name is required.', 'error'); return; }
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description.trim(),
                isFree: form.isFree,
                price: form.price === '' ? 0 : Number(form.price),
                pricePeriod: form.pricePeriod.trim(),
                vehiclesLimit: form.vehiclesLimit === '' ? -1 : Number(form.vehiclesLimit),
                operatorsLimit: form.operatorsLimit === '' ? -1 : Number(form.operatorsLimit),
                routesLimit: form.routesLimit === '' ? -1 : Number(form.routesLimit),
                usersLimit: form.usersLimit === '' ? -1 : Number(form.usersLimit),
                isActive: form.isActive,
            };
            console.log("Saving Price Plan Payload:", payload);
            const url = isEditMode
                ? `/api/price-plans/${editPlan._id}`
                : '/api/price-plans';
            const res = await fetch(url, {
                method: isEditMode ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'adminName': `${user?.firstName} ${user?.lastName}`,
                    'adminEmail': user?.email
                },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (res.ok) {
                showToast(isEditMode ? 'Plan updated!' : 'Plan created successfully!', 'success');
                if (!isEditMode) setForm(defaultForm);
                if (onSaved) onSaved(data.plan);
            } else {
                showToast(data.message || 'Failed to save', 'error');
            }
        } catch {
            showToast('Server error. Is the backend running?', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="cpp-container">
            {/* Toast */}
            {toast && (
                <div className={`pp-toast ${toast.type}`}>
                    {toast.msg}
                </div>
            )}

            <div className="cpp-header">
                <h1 className="cpp-title">{isEditMode ? 'Edit Plan' : 'Create Plan'}</h1>
            </div>

            <div className="cpp-form-card">
                <div className="cpp-row">
                    <div className="cpp-field full">
                        <label className="cpp-label">Plan Name<span className="cpp-required">*</span></label>
                        <input
                            className="cpp-input"
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder="Plan Name"
                        />
                    </div>
                </div>

                <div className="cpp-row">
                    <div className="cpp-field full">
                        <label className="cpp-label">Description</label>
                        <textarea
                            className="cpp-input cpp-textarea"
                            name="description"
                            value={form.description}
                            onChange={handleChange}
                            placeholder="Description"
                            rows={3}
                        />
                    </div>
                </div>

                <div className="cpp-row">
                    <div className="cpp-field quarter flex-row">
                        <input
                            type="checkbox"
                            className="cpp-checkbox"
                            id="isFree"
                            checked={form.isFree}
                            onChange={(e) => setForm(f => ({ ...f, isFree: e.target.checked }))}
                        />
                        <label htmlFor="isFree" className="cpp-checkbox-label">Is Free</label>
                    </div>

                    <div className="cpp-field quarter">
                        <label className="cpp-label">Plan Price<span className="cpp-required">*</span></label>
                        <input
                            className="cpp-input"
                            type="number"
                            name="price"
                            value={form.price}
                            onChange={handleChange}
                            placeholder="Plan Price"
                            disabled={form.isFree}
                        />
                    </div>

                    <div className="cpp-field quarter">
                        <label className="cpp-label">Billing Period<span className="cpp-required">*</span></label>
                        <select
                            className="cpp-input"
                            name="pricePeriod"
                            value={form.pricePeriod}
                            onChange={handleChange}
                        >
                            <option value="">Select Period</option>
                            <option value="Daily">Daily</option>
                            <option value="Weekly">Weekly</option>
                            <option value="Monthly">Monthly</option>
                            <option value="Quarterly">Quarterly</option>
                            <option value="Half-Yearly">Half-Yearly</option>
                            <option value="Yearly">Yearly</option>
                        </select>
                    </div>

                    <div className="cpp-field quarter">
                        <label className="cpp-label">Vehicles Limit<span className="cpp-required">*</span></label>
                        <input
                            className="cpp-input"
                            type="number"
                            name="vehiclesLimit"
                            value={form.vehiclesLimit}
                            onChange={handleChange}
                            placeholder="Vehicles Limit (enter -1 for unlimited)"
                        />
                        <span className="cpp-hint">Use -1 to indicate unlimited.</span>
                    </div>
                </div>

                <div className="cpp-row">
                    <div className="cpp-field quarter">
                        <label className="cpp-label">Operators Limit<span className="cpp-required">*</span></label>
                        <input
                            className="cpp-input"
                            type="number"
                            name="operatorsLimit"
                            value={form.operatorsLimit}
                            onChange={handleChange}
                            placeholder="Operators Limit"
                        />
                        <span className="cpp-hint">Use -1 to indicate unlimited.</span>
                    </div>

                    <div className="cpp-field quarter">
                        <label className="cpp-label">Routes Limit<span className="cpp-required">*</span></label>
                        <input
                            className="cpp-input"
                            type="number"
                            name="routesLimit"
                            value={form.routesLimit}
                            onChange={handleChange}
                            placeholder="Routes Limit"
                        />
                        <span className="cpp-hint">Use -1 to indicate unlimited.</span>
                    </div>

                    <div className="cpp-field quarter">
                        <label className="cpp-label">Users Limit<span className="cpp-required">*</span></label>
                        <input
                            className="cpp-input"
                            type="number"
                            name="usersLimit"
                            value={form.usersLimit}
                            onChange={handleChange}
                            placeholder="Users Limit"
                        />
                        <span className="cpp-hint">Use -1 to indicate unlimited.</span>
                    </div>

                    <div className="cpp-field quarter flex-row">
                        {/* Placeholder for "Color" like in user image or just space */}
                    </div>
                </div>

                <div className="cpp-actions">
                    <button className="cpp-save-btn" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : (isEditMode ? 'Update Plan' : 'Create Plan')}
                    </button>
                </div>
            </div>

            <style>{`
                .cpp-container {
                    padding: 40px;
                    background: #f1f4f9;
                    min-height: 100vh;
                    font-family: 'Inter', sans-serif;
                }
                .cpp-header { margin-bottom: 24px; }
                .cpp-title { font-size: 20px; font-weight: 600; color: #434a60; }
                
                .cpp-form-card {
                    background: white;
                    padding: 30px;
                    border-radius: 12px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                }
                
                .cpp-row {
                    display: flex;
                    gap: 20px;
                    margin-bottom: 20px;
                }
                .cpp-field { display: flex; flex-direction: column; }
                .cpp-field.full { flex: 1; }
                .cpp-field.quarter { flex: 1; width: 0; }
                .cpp-field.flex-row { flex-direction: row; align-items: center; gap: 10px; }
                
                .cpp-label {
                    font-size: 13px;
                    font-weight: 600;
                    color: #555;
                    margin-bottom: 8px;
                }
                .cpp-required { color: #e53e3e; margin-left: 2px; }
                
                .cpp-input {
                    padding: 10px 12px;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    font-size: 13px;
                    color: #434a60;
                    outline: none;
                    transition: border-color 0.2s;
                }
                .cpp-input:focus { border-color: #3f51b5; }
                .cpp-input::placeholder { color: #a0aec0; }
                
                .cpp-textarea { resize: vertical; min-height: 80px; }
                
                .cpp-checkbox {
                    width: 18px;
                    height: 18px;
                    cursor: pointer;
                }
                .cpp-checkbox-label {
                    font-size: 13px;
                    color: #434a60;
                    cursor: pointer;
                }
                
                .cpp-hint {
                    font-size: 11px;
                    color: #718096;
                    margin-top: 4px;
                    font-style: italic;
                }
                
                .cpp-actions {
                    margin-top: 30px;
                    display: flex;
                    justify-content: flex-end;
                }
                .cpp-save-btn {
                    background: #3f51b5;
                    color: white;
                    border: none;
                    padding: 10px 30px;
                    border-radius: 6px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.2s;
                }
                .cpp-save-btn:hover { background: #303f9f; }
                .cpp-save-btn:disabled { background: #a0aec0; cursor: not-allowed; }

                .pp-toast {
                    position: fixed; top: 20px; right: 20px;
                    padding: 12px 24px; border-radius: 8px;
                    background: #333; color: white;
                    z-index: 1000;
                }
                .pp-toast.success { background: #10b981; }
                .pp-toast.error { background: #e53e3e; }

                /* --- Dark Mode Overrides --- */
                .theme-dark .cpp-container {
                    background: #0f0f0f;
                }
                .theme-dark .cpp-title {
                    color: #e2e8f0;
                }
                .theme-dark .cpp-form-card {
                    background: #161616;
                    border: 1px solid #222;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.3);
                }
                .theme-dark .cpp-label {
                    color: #888;
                }
                .theme-dark .cpp-checkbox-label {
                    color: #ccc;
                }
                .theme-dark .cpp-input {
                    background: #111;
                    border-color: #2a2a2a;
                    color: #ddd;
                }
                .theme-dark .cpp-input:focus {
                    border-color: #3f51b5;
                }
                .theme-dark .cpp-input::placeholder {
                    color: #444;
                }
                .theme-dark .cpp-hint {
                    color: #555;
                }
            `}</style>
        </div>
    );
}
